import React, { useEffect } from 'react';

interface SheetProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    children: React.ReactNode;
}

export function Sheet({ isOpen, onClose, title, description, children }: SheetProps) {
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/80 backdrop-blur-md transition-opacity"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Panel */}
            <div className="relative z-50 w-full max-w-lg h-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border-l border-white/10 shadow-2xl animate-slide-in-right flex flex-col backdrop-blur-xl">
                {/* Header */}
                <div className="px-10 py-8 border-b border-white/10 bg-white/5 backdrop-blur-md flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-white font-oswald uppercase tracking-tight">{title}</h2>
                        {description && (
                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald mt-2">{description}</p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-3 text-gray-400 hover:text-white hover:bg-white/10 rounded-2xl transition-all border border-white/5 hover:border-white/10"
                        aria-label="Fechar"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-10 custom-scrollbar">
                    {children}
                </div>
            </div>
        </div>
    );
}
